import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { InsightBar, statusColor } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { FulfillOrderModal } from './FulfillOrderModal.jsx';
import { OrderCreateModal } from './OrderCreateModal.jsx';
import { OrderSendModal } from './OrderSendModal.jsx';

export function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const palette = useChartPalette();
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);

  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['sales', 'orders', statusFilter],
    queryFn: () => salesApi.listOrders({ status: statusFilter || undefined }),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const { data: insights } = useQuery({
    queryKey: ['sales', 'order-insights'],
    queryFn: () => salesApi.orderInsights().then((r) => r.items),
  });
  const byStatus = insights?.byStatus || [];

  const confirm = useMutation({
    mutationFn: (id) => salesApi.confirmOrder(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'orders'] }),
  });
  const cancel = useMutation({
    mutationFn: (id) => salesApi.cancelOrder(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'orders'] }),
  });

  const columns = [
    {
      key: 'orderNumber',
      label: t('col.number'),
      render: (r) => <Link to={`/sales/orders/${r.id}`}>{r.orderNumber}</Link>,
    },
    { key: 'orderDate',   label: t('col.date'), render: (r) => formatDate(r.orderDate) },
    {
      key: 'totalAmount',
      label: t('col.total'),
      align: 'right',
      render: (r) => formatCurrency(r.totalAmount, r.currency),
    },
    { key: 'fulfillmentStatus', label: t('sales.orders.col.fulfillment'), render: (r) => <Badge status={r.fulfillmentStatus} /> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    {
      key: 'actions',
      label: '',
      render: (r) => {
        if (r.status === 'DRAFT') {
          return (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button
                size="sm"
                variant="primary"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate(r.id)}
              >
                {t('sales.orders.action.confirm')}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(r.id)}
              >
                {t('sales.orders.action.cancel')}
              </Button>
            </div>
          );
        }
        if (r.status === 'CONFIRMED') {
          return (
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" variant="primary" onClick={() => setFulfillTarget(r)}>
                {t('sales.orders.action.fulfill')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSendTarget(r)}>
                {t('sales.orders.action.email')}
              </Button>
              <Button size="sm" variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate(r.id)}>
                {t('sales.orders.action.cancel')}
              </Button>
            </div>
          );
        }
        return null;
      },
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('sales.orders.title')}</h1>
          <p className="muted">{t('sales.orders.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('sales.orders.action.new')}
          </Button>
        </div>
      </header>

      {byStatus.length > 0 && (
        <InsightBar
          title={t('sales.orders.insight.dist')}
          labels={byStatus.map((b) => t(`status.${b.status}`))}
          values={byStatus.map((b) => Number(b.amount))}
          colors={byStatus.map((b) => statusColor(palette, b.status))}
          formatValue={(n) => formatCurrency(n)}
          metrics={[
            { label: t('sales.orders.insight.open'), value: insights.open.count },
            { label: t('sales.orders.insight.openValue'), value: formatCurrency(insights.open.amount) },
            { label: t('sales.orders.insight.toFulfill'), value: formatCurrency(insights.toFulfill.amount) },
            { label: t('sales.orders.insight.booked'), value: formatCurrency(insights.booked) },
          ]}
        />
      )}

      <div className="toolbar">
        <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t('finance.invoices.filterStatus.all')}</option>
          <option value="DRAFT">{t('status.DRAFT')}</option>
          <option value="CONFIRMED">{t('status.CONFIRMED')}</option>
          <option value="FULFILLED">{t('status.FULFILLED')}</option>
          <option value="INVOICED">{t('status.INVOICED')}</option>
          <option value="CANCELLED">{t('status.CANCELLED')}</option>
        </select>
      </div>
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('empty.orders')} onRowClick={(r) => navigate(`/sales/orders/${r.id}`)} />
      <OrderCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <FulfillOrderModal order={fulfillTarget} onClose={() => setFulfillTarget(null)} />
      <OrderSendModal order={sendTarget} onClose={() => setSendTarget(null)} />
    </section>
  );
}
